// Connectivity Register — internet / network connections of the Local Body
// (G.O.(Rt) No. 717/2026/LSGD, Date: 12.03.2026)
import React, { useMemo, useState } from "react";
import {
  useRegisterList, RegisterHeader, SearchBox, RegisterPrintActions,
  RegisterTable, RegisterEntryDialog, AddEntryButton, GovFooterNote, fetchBundle, useLocalBodyName,
} from "./shared";
import { sectionsForOption } from "@/lib/registerPdf";

const EMPTY = {
  connection_type: "", service_provider: "", connection_id: "", location: "",
  bandwidth: "", installation_date: "", monthly_cost: "", contact_number: "",
  router_modem_details: "", status: "", remarks: "",
};

const FIELDS = [
  { name: "connection_type", label: "Connection Type", type: "select", options: ["Broadband (FTTH)", "Broadband (DSL)", "Leased Line", "4G/5G Dongle", "Wi-Fi Hotspot", "VSAT", "Other"] },
  { name: "service_provider", label: "Service Provider" },
  { name: "connection_id", label: "Connection ID / Account Number" },
  { name: "location", label: "Location" },
  { name: "bandwidth", label: "Bandwidth / Speed" },
  { name: "installation_date", label: "Installation Date", type: "date" },
  { name: "monthly_cost", label: "Monthly Cost" },
  { name: "contact_number", label: "Contact Number" },
  { name: "status", label: "Status", type: "select", options: ["Active", "Inactive", "Suspended", "Disconnected"] },
  { name: "router_modem_details", label: "Router / Modem Details", type: "textarea" },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

export default function ConnectivityRegisterTab() {
  const { rows, loading, save, remove } = useRegisterList("connectivity");
  const localBody = useLocalBodyName();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => [r.connection_type, r.service_provider, r.connection_id, r.location, r.status]
      .some(v => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  return (
    <div className="space-y-4" data-testid="connectivity-register-tab">
      <RegisterHeader
        title="CONNECTIVITY REGISTER"
        formatNo="G.O.(Rt) 717/2026/LSGD"
        localBody={localBody}
        subtitle="Internet and network connections of the Local Body"
        testid="connectivity-register"
      >
        <SearchBox value={search} onChange={setSearch} placeholder="Search provider, connection ID, location…" testid="connectivity-search" />
        <RegisterPrintActions sections={sectionsForOption("connectivity")} getBundle={fetchBundle} filename="Connectivity_Register.pdf" testid="connectivity-print" />
        <AddEntryButton onClick={() => setEditing({ ...EMPTY })} label="Add Connection" testid="connectivity-add-btn" />
      </RegisterHeader>

      <RegisterTable
        testid="connectivity-table"
        loading={loading}
        rows={filtered}
        emptyText="No connections recorded yet."
        columns={[
          { key: "sl_no", label: "Sl.", render: (r, i) => r.sl_no || i + 1 },
          { key: "connection_type", label: "Type" },
          { key: "service_provider", label: "Provider" },
          { key: "connection_id", label: "Connection ID", render: r => <span className="font-mono text-xs">{r.connection_id || "—"}</span> },
          { key: "location", label: "Location" },
          { key: "bandwidth", label: "Bandwidth" },
          { key: "monthly_cost", label: "Monthly Cost" },
          { key: "status", label: "Status" },
        ]}
        onEdit={r => setEditing({ ...EMPTY, ...r })}
        onDelete={async r => { if (window.confirm("Delete this connection entry?")) await remove(r); }}
      />
      <GovFooterNote />

      <RegisterEntryDialog
        open={!!editing}
        title={editing?.id ? "Edit Connection" : "Add Connection"}
        fields={FIELDS}
        values={editing || EMPTY}
        onChange={setEditing}
        onClose={() => setEditing(null)}
        onSave={async () => { if (await save(editing, editing?.id ? editing : null)) setEditing(null); }}
        testid="connectivity-dialog"
      />
    </div>
  );
}
